/**
 * Aggregation type definitions for Report Builder GROUP BY feature.
 *
 * Maps OpenSearch field types → available aggregation functions.
 * Used by SelectedColumnsBar (per-column agg dropdown) and PreviewTable (query builder).
 */

/** Supported aggregation functions */
export type AggregationType =
  'count' | 'sum' | 'avg' | 'min' | 'max' | 'cardinality' | 'percent_true';

/** Human-readable labels for aggregation types */
export const AGG_LABELS: Record<AggregationType, string> = {
  count: 'Count',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
  cardinality: 'Distinct Count',
  percent_true: '% true',
};

/** Aggregation options available per field type */
const AGG_OPTIONS_BY_TYPE: Record<string, AggregationType[]> = {
  // Numeric fields support all metric aggregations
  integer: ['count', 'sum', 'avg', 'min', 'max', 'cardinality'],
  long: ['count', 'sum', 'avg', 'min', 'max', 'cardinality'],
  float: ['count', 'sum', 'avg', 'min', 'max', 'cardinality'],
  double: ['count', 'sum', 'avg', 'min', 'max', 'cardinality'],
  half_float: ['count', 'sum', 'avg', 'min', 'max', 'cardinality'],
  scaled_float: ['count', 'sum', 'avg', 'min', 'max', 'cardinality'],

  // Date fields support min/max (earliest/latest) and count
  date: ['count', 'min', 'max', 'cardinality'],

  // Keyword/text fields only support count and distinct count
  keyword: ['count', 'cardinality'],
  text: ['count', 'cardinality'],

  // `percent_true` is an `avg` over the boolean: OpenSearch's fraction of true docs.
  boolean: ['count', 'percent_true', 'cardinality'],
};

/** Default aggregation for each field type (used when group-by is first activated) */
const DEFAULT_AGG_BY_TYPE: Record<string, AggregationType> = {
  integer: 'sum',
  long: 'sum',
  float: 'sum',
  double: 'sum',
  half_float: 'sum',
  scaled_float: 'sum',
  date: 'max',
  keyword: 'count',
  text: 'count',
  boolean: 'count',
};

/** Returns aggregations for a lowercase OpenSearch field type; unknown types allow count and cardinality. */
export function getAggOptionsForType(fieldType: string): AggregationType[] {
  return AGG_OPTIONS_BY_TYPE[fieldType] || ['count', 'cardinality'];
}

/** Defaults numeric OpenSearch types to sum and dates to max; other or unknown types use count. */
export function getDefaultAggForType(fieldType: string): AggregationType {
  return DEFAULT_AGG_BY_TYPE[fieldType] || 'count';
}

/** Serialized column aggregation map: columnKey → AggregationType */
export type ColumnAggregationMap = Record<string, AggregationType>;

/** Date grouping intervals for date_histogram aggregations */
export type DateGroupingInterval = 'year' | 'quarter' | 'month' | 'week' | 'day';

/** Available date grouping interval options for dropdowns */
export const DATE_GROUPING_OPTIONS: Array<{ key: DateGroupingInterval; label: string }> = [
  { key: 'year', label: 'Year' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'month', label: 'Month' },
  { key: 'week', label: 'Week' },
  { key: 'day', label: 'Day' },
];

/** Map of group-by fieldKey → DateGroupingInterval (only for date fields) */
export type DateGroupingMap = Record<string, DateGroupingInterval>;
